'use client';

import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
import { ChevronDownIcon, CircleX } from 'lucide-react';
import * as React from 'react';

import { Button } from '@/shared/shadcn/ui/button';
import { Calendar } from '@/shared/shadcn/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/shadcn/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/shadcn/ui/select';
import { TZDate } from '@date-fns/tz';
import { timezoneToStringOffset } from '../utils/format-date';

interface DateTimePickerProps {
  date: Date | string | undefined | null;
  onDateChange: (date: Date | undefined | null) => void;
  name?: string;
  withClear?: boolean;
  timezone?: number;
}

export function DateTimePicker({ date, onDateChange, name = 'datetime', timezone, withClear }: DateTimePickerProps) {
  const [open, setOpen] = React.useState(false);
  const value = date ? new TZDate(date as string, timezoneToStringOffset(timezone)) : undefined;

  const handleDateChange = (newDate: Date | undefined) => {
    onDateChange(timezone && newDate ? new TZDate(newDate, 'UTC') : newDate);
  };

  const handleTimeChange = (part: 'hours' | 'minutes', valueStr: string) => {
    const numericValue = parseInt(valueStr, 10);
    const newDate = value ? new Date(value.getTime()) : new Date();

    if (!date) {
      newDate.setHours(0, 0, 0, 0);
    }

    if (part === 'hours') {
      newDate.setHours(numericValue);
    } else if (part === 'minutes') {
      newDate.setMinutes(numericValue);
    }

    handleDateChange(newDate);
  };

  const handleTimeClear = () => {
    onDateChange(null);
  };

  return (
    <div className="flex items-center gap-4">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button variant="outline" id={`${name}-date-picker`} className="w-36 justify-between font-normal">
            {value ? format(value, 'dd.MM.yy', { locale: ru }) : 'Дата'}
            <ChevronDownIcon />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto overflow-hidden p-0" align="start">
          <Calendar
            mode="single"
            selected={value}
            captionLayout="dropdown"
            onSelect={handleDateChange}
            timeZone={timezoneToStringOffset(timezone)}
          />
        </PopoverContent>
      </Popover>
      <div className="flex items-center gap-1">
        <Select value={value ? String(value.getHours()) : '0'} onValueChange={(val) => handleTimeChange('hours', val)}>
          <SelectTrigger id={`${name}-hours-picker`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {Array.from({ length: 24 }, (_, i) => (
              <SelectItem key={i} value={String(i)}>
                {String(i).padStart(2, '0')}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <span className="text-muted-foreground">:</span>
        <Select
          value={value ? String(value.getMinutes()) : '0'}
          onValueChange={(val) => handleTimeChange('minutes', val)}
        >
          <SelectTrigger id={`${name}-minutes-picker`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {Array.from({ length: 60 }, (_, i) => (
              <SelectItem key={i} value={String(i)}>
                {String(i).padStart(2, '0')}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {withClear && value && (
        <Button
          variant="ghost"
          size="icon"
          className="text-muted-foreground hover:text-foreground h-8 w-8"
          type="button"
          onClick={handleTimeClear}
          title="Очистить дату"
        >
          <CircleX className="h-4 w-4" />
        </Button>
      )}
    </div>
  );
}
